import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, Building2, X, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const fmtMonto = (v) =>
  Number(v).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });

const fmtFecha = (v) =>
  new Date(v + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

// ── Formulario nueva cuenta bancaria ─────────────────────────────────────
const emptyCuenta = { banco: '', nombre_cuenta: '', numero_cuenta: '', cbu: '' };

function CuentaForm({ form, onChange }) {
  const setEv = (f) => (e) => onChange({ ...form, [f]: e.target.value });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Banco *</label>
          <input
            className="input"
            placeholder="Ej: Galicia, BBVA, Santander..."
            value={form.banco}
            onChange={setEv('banco')}
          />
        </div>
        <div>
          <label>Nombre de cuenta *</label>
          <input
            className="input"
            placeholder="Ej: Cuenta corriente"
            value={form.nombre_cuenta}
            onChange={setEv('nombre_cuenta')}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>N.° de cuenta</label>
          <input
            className="input font-mono"
            placeholder="Opcional"
            value={form.numero_cuenta}
            onChange={setEv('numero_cuenta')}
          />
        </div>
        <div>
          <label>CBU</label>
          <input
            className="input font-mono"
            placeholder="22 dígitos"
            maxLength={22}
            value={form.cbu}
            onChange={setEv('cbu')}
          />
        </div>
      </div>
    </div>
  );
}

// ── Formulario nuevo movimiento ──────────────────────────────────────────
const emptyMov = {
  cuenta_id: '', tipo: 'credito', monto: '',
  concepto: '', fecha: new Date().toISOString().slice(0, 10),
};

function MovimientoForm({ form, onChange, cuentas }) {
  const setEv = (f) => (e) => onChange({ ...form, [f]: e.target.value });
  const set   = (f) => (v) => onChange({ ...form, [f]: v });

  return (
    <div className="space-y-4">

      {/* Tipo credito/debito */}
      <div>
        <label>Tipo *</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'credito', label: 'Crédito', icon: ArrowUpCircle },
            { key: 'debito',  label: 'Débito',  icon: ArrowDownCircle },
          ].map(({ key, label, icon: Icon }) => {
            const isActive  = form.tipo === key;
            const isCredito = key === 'credito';
            return (
              <button
                key={key}
                type="button"
                onClick={() => set('tipo')(key)}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                  isActive
                    ? isCredito
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500 dark:text-emerald-400'
                      : 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:border-rose-500 dark:text-rose-400'
                    : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cuenta */}
      <div>
        <label>Cuenta *</label>
        <select className="input" value={form.cuenta_id} onChange={setEv('cuenta_id')}>
          <option value="">— Seleccioná una cuenta —</option>
          {cuentas.map(c => (
            <option key={c.id} value={c.id}>{c.banco} — {c.nombre_cuenta}</option>
          ))}
        </select>
      </div>

      {/* Monto + fecha */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Monto *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              className="input pl-7"
              type="number" min="0" step="0.01" placeholder="0,00"
              value={form.monto}
              onChange={setEv('monto')}
            />
          </div>
        </div>
        <div>
          <label>Fecha *</label>
          <input className="input" type="date" value={form.fecha} onChange={setEv('fecha')} />
        </div>
      </div>

      {/* Concepto */}
      <div>
        <label>Concepto *</label>
        <input
          className="input"
          placeholder="Ej: Transferencia cliente, pago proveedor..."
          value={form.concepto}
          onChange={setEv('concepto')}
          maxLength={255}
        />
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────
export default function Bancos() {
  const { can } = useAuth();

  const [cuentas,        setCuentas]        = useState([]);
  const [selectedId,     setSelectedId]     = useState(null);
  const [movimientos,    setMovimientos]    = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(true);
  const [loadingMov,     setLoadingMov]     = useState(false);

  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: '', tipo: '' });

  const [modalMov,    setModalMov]    = useState(false);
  const [modalCuenta, setModalCuenta] = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [formMov,     setFormMov]     = useState(emptyMov);
  const [formCuenta,  setFormCuenta]  = useState(emptyCuenta);
  const [saving,      setSaving]      = useState(false);

  const [confirmMovId,    setConfirmMovId]    = useState(null);
  const [confirmCuentaId, setConfirmCuentaId] = useState(null);
  const [deleting,        setDeleting]        = useState(false);

  const puedeGestionar = can('tesoreria.movimientos');

  // ── Cargar cuentas ──
  const fetchCuentas = useCallback(async () => {
    setLoadingCuentas(true);
    try {
      const res = await api.get('/cuentas-bancarias/index.php');
      setCuentas(res.data);
      if (!selectedId && res.data.length > 0) setSelectedId(res.data[0].id);
    } catch { toast.error('Error al cargar cuentas bancarias'); }
    finally { setLoadingCuentas(false); }
  }, [selectedId]);

  useEffect(() => { fetchCuentas(); }, []); // eslint-disable-line

  // ── Cargar movimientos ──
  const fetchMovimientos = useCallback(async () => {
    if (!selectedId) return;
    setLoadingMov(true);
    try {
      const params = { cuenta_id: selectedId };
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.tipo)        params.tipo        = filtros.tipo;
      const res = await api.get('/movimientos-bancarios/index.php', { params });
      setMovimientos(res.data);
    } catch { toast.error('Error al cargar movimientos'); }
    finally { setLoadingMov(false); }
  }, [selectedId, filtros]);

  useEffect(() => { fetchMovimientos(); }, [fetchMovimientos]);

  // ── Nuevo movimiento ──
  const openMovModal = () => {
    setFormMov({ ...emptyMov, cuenta_id: selectedId ?? '' });
    setModalMov(true);
  };

  const handleSaveMov = async () => {
    if (!formMov.cuenta_id)                              { toast.error('Seleccioná una cuenta'); return; }
    if (!formMov.monto || parseFloat(formMov.monto) <= 0) { toast.error('El monto debe ser mayor a cero'); return; }
    if (!formMov.concepto.trim())                        { toast.error('El concepto es requerido'); return; }
    setSaving(true);
    try {
      await api.post('/movimientos-bancarios/index.php', {
        cuenta_id: parseInt(formMov.cuenta_id),
        tipo:      formMov.tipo,
        monto:     parseFloat(formMov.monto),
        concepto:  formMov.concepto.trim(),
        fecha:     formMov.fecha || null,
      });
      toast.success('Movimiento registrado');
      setModalMov(false);
      await Promise.all([fetchCuentas(), fetchMovimientos()]);
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // ── Nueva / editar cuenta ──
  const openCuentaModal = (cuenta = null) => {
    setEditingId(cuenta?.id ?? null);
    setFormCuenta(cuenta
      ? { banco: cuenta.banco, nombre_cuenta: cuenta.nombre_cuenta, numero_cuenta: cuenta.numero_cuenta ?? '', cbu: cuenta.cbu ?? '' }
      : emptyCuenta
    );
    setModalCuenta(true);
  };

  const handleSaveCuenta = async () => {
    if (!formCuenta.banco.trim())         { toast.error('El banco es requerido'); return; }
    if (!formCuenta.nombre_cuenta.trim()) { toast.error('El nombre de cuenta es requerido'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/cuentas-bancarias/index.php?id=${editingId}`, formCuenta);
        toast.success('Cuenta actualizada');
      } else {
        await api.post('/cuentas-bancarias/index.php', formCuenta);
        toast.success('Cuenta creada');
      }
      setModalCuenta(false);
      fetchCuentas();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // ── Eliminar movimiento ──
  const handleDeleteMov = async () => {
    setDeleting(true);
    try {
      await api.delete(`/movimientos-bancarios/index.php?id=${confirmMovId}`);
      toast.success('Movimiento eliminado');
      setConfirmMovId(null);
      await Promise.all([fetchCuentas(), fetchMovimientos()]);
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  // ── Eliminar cuenta ──
  const handleDeleteCuenta = async () => {
    setDeleting(true);
    try {
      await api.delete(`/cuentas-bancarias/index.php?id=${confirmCuentaId}`);
      toast.success('Cuenta eliminada');
      setConfirmCuentaId(null);
      if (selectedId === confirmCuentaId) setSelectedId(null);
      fetchCuentas();
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  // ── Totales ──
  const totalCreditos = movimientos.filter(m => m.tipo === 'credito').reduce((s, m) => s + m.monto, 0);
  const totalDebitos  = movimientos.filter(m => m.tipo === 'debito').reduce((s, m) => s + m.monto, 0);
  const saldoPeriodo  = totalCreditos - totalDebitos;
  const tieneFiltro   = filtros.fecha_desde || filtros.fecha_hasta || filtros.tipo;
  const cuentaActual  = cuentas.find(c => c.id === selectedId);

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Bancos</h1>
        {puedeGestionar && (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => openCuentaModal()}>
              <Plus size={16} /> Nueva cuenta
            </button>
            <button className="btn-primary" onClick={openMovModal} disabled={!selectedId}>
              <Plus size={18} /> Nuevo movimiento
            </button>
          </div>
        )}
      </div>

      {/* Selector de cuentas */}
      {loadingCuentas ? (
        <div className="flex gap-3">
          {[1, 2].map(i => (
            <div key={i} className="h-28 w-56 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      ) : cuentas.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12 text-center">
          <Building2 size={36} strokeWidth={1.5} className="text-slate-200 dark:text-slate-700 mb-3" />
          <p className="text-sm text-slate-400">No hay cuentas bancarias configuradas.</p>
          {puedeGestionar && (
            <button className="btn-primary mt-4" onClick={() => openCuentaModal()}>
              <Plus size={16} /> Agregar primera cuenta
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {cuentas.map(cuenta => {
            const isSelected = cuenta.id === selectedId;
            const saldoNeg   = cuenta.saldo < 0;
            return (
              <button
                key={cuenta.id}
                onClick={() => setSelectedId(cuenta.id)}
                className={`relative flex flex-col gap-2 px-5 py-4 rounded-2xl text-left transition-all duration-150 min-w-[200px] group
                  ${isSelected
                    ? 'bg-primary-500 shadow-card-md'
                    : 'bg-white dark:bg-slate-800 shadow-card hover:shadow-card-md hover:-translate-y-px'
                  }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-white/20' : 'bg-primary-50 dark:bg-primary-500/10'
                  }`}>
                    <Building2 size={15} className={isSelected ? 'text-white' : 'text-primary-500 dark:text-primary-400'} />
                  </div>
                  {puedeGestionar && (
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openCuentaModal(cuenta); }}
                        className={`p-1 rounded-lg transition-colors ${
                          isSelected
                            ? 'hover:bg-white/20 text-white/60 hover:text-white'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-300 hover:text-slate-600 dark:hover:text-slate-200'
                        }`}
                      >
                        <Pencil size={11} />
                      </button>
                      {cuentas.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setConfirmCuentaId(cuenta.id); }}
                          className={`p-1 rounded-lg transition-colors ${
                            isSelected
                              ? 'hover:bg-white/20 text-white/60 hover:text-white'
                              : 'hover:bg-red-50 dark:hover:bg-red-500/10 text-slate-300 hover:text-red-500'
                          }`}
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <p className={`text-[12px] font-bold leading-none mb-0.5 ${
                    isSelected ? 'text-white' : 'text-slate-900 dark:text-white'
                  }`}>
                    {cuenta.banco}
                  </p>
                  <p className={`text-[11px] mb-2 ${
                    isSelected ? 'text-white/70' : 'text-slate-400'
                  }`}>
                    {cuenta.nombre_cuenta}
                  </p>
                  {cuenta.cbu && (
                    <p className={`text-[10px] font-mono mb-2 ${
                      isSelected ? 'text-white/50' : 'text-slate-300 dark:text-slate-600'
                    }`}>
                      CBU ···{cuenta.cbu.slice(-4)}
                    </p>
                  )}
                  <p className={`text-lg font-bold tabular-nums leading-none ${
                    isSelected ? 'text-white' : saldoNeg ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'
                  }`}>
                    {fmtMonto(cuenta.saldo)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filtros + resumen */}
      {selectedId && (
        <div className="card flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Desde</label>
            <input
              className="input"
              type="date"
              value={filtros.fecha_desde}
              onChange={(e) => setFiltros(f => ({ ...f, fecha_desde: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Hasta</label>
            <input
              className="input"
              type="date"
              value={filtros.fecha_hasta}
              onChange={(e) => setFiltros(f => ({ ...f, fecha_hasta: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Tipo</label>
            <select
              className="input"
              value={filtros.tipo}
              onChange={(e) => setFiltros(f => ({ ...f, tipo: e.target.value }))}
            >
              <option value="">Todos</option>
              <option value="credito">Créditos</option>
              <option value="debito">Débitos</option>
            </select>
          </div>
          {tieneFiltro && (
            <button
              className="btn-secondary text-sm"
              onClick={() => setFiltros({ fecha_desde: '', fecha_hasta: '', tipo: '' })}
            >
              Limpiar
            </button>
          )}

          {movimientos.length > 0 && (
            <div className="ml-auto flex items-center gap-5">
              <div className="text-right">
                <p className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">Créditos</p>
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{fmtMonto(totalCreditos)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider">Débitos</p>
                <p className="text-sm font-bold text-rose-600 dark:text-rose-400 tabular-nums">{fmtMonto(totalDebitos)}</p>
              </div>
              <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />
              <div className="text-right">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Neto período</p>
                <p className={`text-sm font-bold tabular-nums ${saldoPeriodo >= 0 ? 'text-slate-900 dark:text-white' : 'text-rose-600 dark:text-rose-400'}`}>
                  {fmtMonto(saldoPeriodo)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabla de movimientos */}
      {selectedId && (
        <div className="card !p-0 overflow-hidden">
          {loadingMov ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : movimientos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-700">
              <Building2 size={48} strokeWidth={1.5} />
              <p className="text-sm mt-3 text-slate-400">
                {tieneFiltro
                  ? 'Sin movimientos para los filtros aplicados'
                  : `Sin movimientos en ${cuentaActual?.banco} — ${cuentaActual?.nombre_cuenta}`}
              </p>
              {puedeGestionar && !tieneFiltro && (
                <button className="btn-primary mt-4" onClick={openMovModal}>
                  <Plus size={16} /> Registrar primero
                </button>
              )}
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th className="text-right">Monto</th>
                    <th>Usuario</th>
                    {puedeGestionar && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => (
                    <tr key={m.id}>
                      <td className="whitespace-nowrap text-slate-500 text-sm">
                        {fmtFecha(m.fecha)}
                      </td>
                      <td>
                        {m.tipo === 'credito' ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-200/70 dark:ring-emerald-500/30">
                            <ArrowUpCircle size={11} /> Crédito
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 ring-1 ring-rose-200/70 dark:ring-rose-500/30">
                            <ArrowDownCircle size={11} /> Débito
                          </span>
                        )}
                      </td>
                      <td className="text-slate-700 dark:text-slate-300 max-w-[280px] truncate">{m.concepto}</td>
                      <td className={`text-right font-semibold tabular-nums ${
                        m.tipo === 'credito' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                      }`}>
                        {m.tipo === 'debito' && '−'}{fmtMonto(m.monto)}
                      </td>
                      <td className="text-slate-400 text-sm">{m.usuario}</td>
                      {puedeGestionar && (
                        <td>
                          <button
                            onClick={() => setConfirmMovId(m.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal: nuevo movimiento */}
      <Modal
        isOpen={modalMov}
        onClose={() => setModalMov(false)}
        title="Nuevo movimiento bancario"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalMov(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSaveMov} disabled={saving}>
              {saving ? 'Guardando...' : 'Registrar movimiento'}
            </button>
          </>
        }
      >
        <MovimientoForm form={formMov} onChange={setFormMov} cuentas={cuentas} />
      </Modal>

      {/* Modal: nueva / editar cuenta */}
      <Modal
        isOpen={modalCuenta}
        onClose={() => setModalCuenta(false)}
        title={editingId ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalCuenta(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSaveCuenta} disabled={saving}>
              {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear cuenta'}
            </button>
          </>
        }
      >
        <CuentaForm form={formCuenta} onChange={setFormCuenta} />
      </Modal>

      {/* Confirm: eliminar movimiento */}
      <ConfirmDialog
        isOpen={!!confirmMovId}
        onClose={() => setConfirmMovId(null)}
        onConfirm={handleDeleteMov}
        loading={deleting}
        title="Eliminar movimiento"
        message="¿Seguro que querés eliminar este movimiento? El saldo de la cuenta se recalculará."
      />

      {/* Confirm: eliminar cuenta */}
      <ConfirmDialog
        isOpen={!!confirmCuentaId}
        onClose={() => setConfirmCuentaId(null)}
        onConfirm={handleDeleteCuenta}
        loading={deleting}
        title="Eliminar cuenta bancaria"
        message="¿Seguro que querés eliminar esta cuenta? Se ocultará pero sus movimientos se conservan."
      />
    </div>
  );
}
